import { useEffect } from 'react';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

type Theme = 'light' | 'dark';

/**
 * Keys for the once-per-tab "what is this?" banners. Adding a new entry
 * here lets a route show an ExplainerBanner that the student can dismiss
 * forever. Persisted to localStorage so the dismissal survives reloads.
 *
 * Spark is deliberately excluded — its `SoftDiagnosticPrompt` (fires at
 * card 5) already serves the "what is this surface for?" job, and the
 * fullscreen-snap layout makes an added banner intrusive.
 */
export type ExplainerKey = 'today' | 'tutor';

interface UiPrefState {
  sidebarCollapsed: boolean;
  theme: Theme;
  dismissedExplainers: Partial<Record<ExplainerKey, string>>;
  toggleSidebar: () => void;
  setSidebar: (collapsed: boolean) => void;
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
  dismissExplainer: (key: ExplainerKey) => void;
}

function initialTheme(): Theme {
  if (typeof document === 'undefined') return 'light';
  return document.documentElement.classList.contains('dark') ? 'dark' : 'light';
}

export const useUiPref = create<UiPrefState>()(
  persist(
    (set) => ({
      sidebarCollapsed: false,
      theme: initialTheme(),
      dismissedExplainers: {},
      toggleSidebar: () => set((s) => ({ sidebarCollapsed: !s.sidebarCollapsed })),
      setSidebar: (collapsed) => set({ sidebarCollapsed: collapsed }),
      setTheme: (theme) => set({ theme }),
      toggleTheme: () => set((s) => ({ theme: s.theme === 'dark' ? 'light' : 'dark' })),
      dismissExplainer: (key) =>
        set((s) => ({
          dismissedExplainers: { ...s.dismissedExplainers, [key]: new Date().toISOString() },
        })),
    }),
    {
      name: 'disha.ui',
      partialize: (s) => ({
        sidebarCollapsed: s.sidebarCollapsed,
        dismissedExplainers: s.dismissedExplainers,
      }),
    },
  ),
);

/**
 * Mirrors the persisted theme to <html class="dark"> and the `disha.theme` key
 * read by the inline boot script in index.html (which prevents FOUC).
 */
export function useThemeEffect() {
  const theme = useUiPref((s) => s.theme);
  useEffect(() => {
    const root = document.documentElement;
    root.classList.toggle('dark', theme === 'dark');
    try {
      localStorage.setItem('disha.theme', theme);
    } catch {}
  }, [theme]);
}
